// Curated COP31 analysis notes shown in the Analysis Layer of /cop31 and
// /cop31-guide. They are surfaced in the essay stream (/arsiv, /archive) and in
// the RSS feeds so the most recent COP31 commentary is discoverable outside
// the guide page itself.

export type Cop31Note = {
  /** Stable slug used for the permalink /cop31/notlar/<slug>; also the stable body/enrichment key. */
  slug: string;
  /** English permalink slug, independent of the stable Turkish/content key. */
  enSlug: string;
  /** ISO date used for sorting and RSS pubDate. */
  iso: string;
  /** Display date, Turkish. */
  dateTr: string;
  /** Display date, English. */
  dateEn: string;
  titleTr: string;
  titleEn: string;
  summaryTr: string;
  summaryEn: string;
  /** Canonical source URL (internal essay, original column, or referenced report). */
  url?: string;
  /** Optional label for the external source shown on the note page and guide card. */
  sourceLabel?: string;
  /** True when the note links to an essay on this site. */
  internal?: boolean;
  /** Notes without a Turkish counterpart are shown only on the English guide. */
  locale?: "both" | "en";
};

export const cop31Notes: Cop31Note[] = [
  {
    iso: "2026-10-07",
    dateTr: "7 Ekim 2026",
    dateEn: "7 October 2026",
    titleTr: "COP31: İklimin siyasi ekonomisi",
    titleEn: "COP31: The Political Economy of Climate",
    summaryTr:
      "Paris iklim diplomasisini karbon üzerine kurdu. Antalya’nın sınavı, dönüşümün maliyetini, riskini, fırsatını ve gücünü adil biçimde paylaşmak.",
    summaryEn:
      "Paris built climate diplomacy around carbon. Antalya's test is to share the transition's costs, risks, opportunities and power fairly.",
    slug: "cop31-iklimin-siyasi-ekonomisi",
    enSlug: "cop31-the-political-economy-of-climate",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/cop31-iklimin-siyasi-ekonomisi-89162",
    sourceLabel: "Ekonomi Gazetesi",
    locale: "both",
  },
  {
    iso: "2026-10-01",
    dateTr: "1 Ekim 2026",
    dateEn: "1 October 2026",
    titleTr: "Antalya’dan önce son durak: Pasifik",
    titleEn: "The Last Stop Before Antalya: The Pacific",
    summaryTr:
      "Fiji, Tuvalu ve Avustralya'nın ortaklaşa düzenlediği resmi Pre-COP süreci 5–8 Ekim'de gerçekleşecek. Pasifik'in Antalya'ya taşıdığı üç öncelik açık: 1,5°C hedefini masada tutmak, iklim finansmanına adil erişim ve okyanusu resmi müzakere gündemine taşımak.",
    summaryEn:
      "The official Pre-COP process co-hosted by Fiji, Tuvalu and Australia will take place on 5–8 October. The Pacific is carrying three clear priorities to Antalya: keeping 1.5°C on the table, securing fair access to climate finance, and moving the ocean onto the formal negotiating agenda.",
    slug: "antalya-dan-once-son-durak-pasifik",
    enSlug: "the-last-stop-before-antalya-the-pacific",
    url: "https://akillisurdurulebilirlik.com/yazilar/antalya-dan-once-son-durak-pasifik",
    internal: true,
  },
  {
    iso: "2026-09-30",
    dateTr: "30 Eylül 2026",
    dateEn: "30 September 2026",
    titleTr: "New York’ta iklimin yeni dili",
    titleEn: "The New Language of Climate in New York",
    summaryTr:
      "New York İklim Haftası’nda Türkiye, COP31 için elektrifikasyondan yapay zekâya, finansmandan şehirlere uzanan bir uygulama gündemi ortaya koydu. Özel sektör de bu kez masadaydı. Antalya’ya giderken asıl soru artık ne vaat edileceği değil, neyin gerçekten uygulanabileceği olacak.",
    summaryEn:
      "At New York Climate Week, Türkiye laid out an implementation agenda for COP31 spanning electrification, artificial intelligence, finance and cities. This time, the private sector was also at the table. On the road to Antalya, the central question is no longer what will be pledged, but what can actually be implemented.",
    slug: "new-yorkta-iklimin-yeni-dili",
    enSlug: "the-new-language-of-climate-in-new-york",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/new-yorkta-iklimin-yeni-dili-88600",
    sourceLabel: "Ekonomi Gazetesi",
  },
  {
    iso: "2026-09-18",
    dateTr: "18 Eylül 2026",
    dateEn: "18 September 2026",
    titleTr: "COP31'in önündeki yeni fay hattı",
    titleEn: "The New Fault Line Ahead of COP31",
    summaryTr:
      "ABD karbonu ekonominin üzerinden kaldırılması gereken bir yük olarak görüyor; Avrupa ise rekabetin yeni giriş koşuluna dönüştürüyor. Dünya, karbonun fiyatı üzerinden iki ayrı ekonomik düzene ayrılıyor. COP31'de ABD'nin sandalyesi boş kalabilir; ama karbonu ucuzlatma politikası masanın tam ortasında olacak.",
    summaryEn:
      "The US views carbon as a burden to be lifted from the economy; Europe is turning it into a new entry condition for competition. The world is splitting into two separate economic orders based on the price of carbon. The US seat may remain empty at COP31, but its policy of cheapening carbon will sit at the centre of the table.",
    slug: "cop31-in-onundeki-yeni-fay-hatti",
    enSlug: "the-new-fault-line-ahead-of-cop31",
    url: "https://akillisurdurulebilirlik.com/yazilar/cop31-in-onundeki-yeni-fay-hatti",
    internal: true,
  },
  {
    iso: "2026-09-09",
    dateTr: "9 Eylül 2026",
    dateEn: "9 September 2026",
    titleTr: "İklim hedefinden rekabet avantajına",
    titleEn: "From Climate Target to Competitive Advantage",
    summaryTr:
      "İklim dönüşümü artık yalnızca çevre politikasının değil, ekonomik rekabetin de merkezinde. İstanbul İklim Finansmanı Zirvesi'nde Türkiye COP31 Başkanlığı'nın gündeme getirdiği İklim Uygulama Köprüsü (BRIDGE), hedeflerle finansman arasındaki kopukluğu — yatırım yapılabilir proje üretme kapasitesini — hedefliyor. Yıllık 7,5–9 trilyon dolarlık ihtiyaca karşılık 1,9 trilyon dolarlık yatırım, açığın yalnızca para değil, öngörülebilir politika ve yapılandırılmış proje meselesi olduğunu gösteriyor.",
    summaryEn:
      "Climate transition now sits at the centre of economic competitiveness, not only environmental policy. At the Istanbul Climate Finance Summit, the Türkiye COP31 Presidency's Climate Implementation Bridge (BRIDGE) targets the disconnect between targets and finance: the missing pipeline of investable projects. Against an annual need of $7.5–9 trillion, actual investment stands near $1.9 trillion — a gap of predictable policy and structured projects as much as of money.",
    slug: "iklim-hedefinden-rekabet-avantajina",
    enSlug: "from-climate-target-to-competitive-advantage",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/iklim-hedefinden-rekabet-avantajina-87087",
    sourceLabel: "Ekonomi Gazetesi",
  },
  {

    iso: "2026-09-06",
    dateTr: "6 Eylül 2026",
    dateEn: "6 September 2026",
    titleTr: "Uçurumdan köprüye: İklim finansmanının yeni dili",
    titleEn: "From Chasm to Bridge: The New Language of Climate Finance",
    summaryTr:
      "İstanbul İklim Finansı Zirvesi'nin iki kelimesi \u2014 u\u00e7urum ve k\u00f6pr\u00fc \u2014 iki farkl\u0131 ikna rejimini anlat\u0131yor: u\u00e7urum vicdanlara, k\u00f6pr\u00fc komitelere konu\u015fur. Finansman a\u00e7\u0131\u011f\u0131n\u0131n bir ucu, bu iki dil aras\u0131ndaki \u00e7eviri a\u00e7\u0131\u011f\u0131d\u0131r.",
    summaryEn:
      "Two words dominated the Istanbul Climate Finance Summit: a chasm and a bridge. They address two different regimes of persuasion \u2014 the chasm speaks to consciences, the bridge to committees. Part of the climate finance gap is not a shortage of money but a shortage of translation between them.",
    slug: "from-chasm-to-bridge",
    enSlug: "from-chasm-to-bridge",
  },
  {
    iso: "2026-09-02",
    dateTr: "2 Eylül 2026",
    dateEn: "2 September 2026",
    titleTr: "Antalya'nın yeni sınavı: 1,5°C aşıldı!",
    titleEn: "Antalya's new test: 1.5°C has been exceeded",
    summaryTr:
      "Dünya 1,5°C'yi aşmaya hazırlanırken, COP31 artık yalnızca hedefi değil, aşımın yüksekliğini, süresini ve geri dönüşünü de yönetmek zorunda. UNEP'in Limiting Overshoot raporu bu yeni gündemin çerçevesini kuruyor.",
    summaryEn:
      "As the world prepares to exceed 1.5°C, COP31 must manage not only the target but the height, duration, and reversal of the overshoot. UNEP's Limiting Overshoot report frames this new agenda.",
    slug: "antalya-nin-yeni-sinavi-1-5-c-asildi",
    enSlug: "antalyas-new-test-1-5c-has-been-exceeded",
    url: "https://akillisurdurulebilirlik.com/yazilar/antalya-nin-yeni-sinavi-1-5-c-asildi",
    internal: true,
  },
  {
    iso: "2026-09-02",
    dateTr: "2 Eylül 2026",
    dateEn: "2 September 2026",
    titleTr: "Aşım COP’u",
    titleEn: "The Overshoot COP",
    summaryTr:
      "UNEP'nin Limiting Overshoot raporu, 1,5°C hedefini artık yalnızca aşılmaması gereken bir sınır değil, yüksekliği, süresi ve kimin kapatacağı belirlenmesi gereken bir aşım penceresi olarak ele alıyor. COP31'in uygulama iddiası, bu borcun geri ödeme takvimini yazıp yazamayacağıyla sınanacak.",
    summaryEn:
      "UNEP's Limiting Overshoot report moves 1.5°C from an absolute boundary to a window whose height, duration, and responsibility must be defined. COP31's claim to be an implementation COP will be tested by whether it can write a repayment schedule for the overshoot itself.",
    slug: "the-overshoot-cop",
    enSlug: "the-overshoot-cop",
    url: "https://www.unep.org/resources/limiting-overshoot-navigating-exceedance",
    sourceLabel: "UNEP — Limiting Overshoot: Navigating Exceedance",
  },
  {
    iso: "2026-08-31",
    dateTr: "31 Ağustos 2026",
    dateEn: "31 August 2026",
    titleTr: "Karbon piyasası nedir?",
    titleEn: "What Is a Carbon Market?",
    summaryTr:
      "Türkiye'nin Emisyon Ticaret Sistemi (ETS) Yönetmeliği yürürlüğe girdi: devlet toplam salıma üst sınır koyuyor, şirketler tahsisat alıp satıyor, kıtlık bilinçli olarak yaratılıyor. Birincil piyasada ihale, ikincil piyasada şirketler arası ticaret; fiyat istikrarı için rezerv ve tavan-taban aralıkları var. COP31 öncesinde Türkiye'nin elindeki en somut politika aracı.",
    summaryEn:
      "Türkiye's Emissions Trading System (ETS) regulation is now in force: the state caps total emissions, companies trade allowances, and scarcity is created deliberately. Allowances are auctioned in the primary market and traded between companies in the secondary market, with a stability reserve and price floors/ceilings. Ahead of COP31 this is Türkiye's most concrete policy instrument.",
    slug: "karbon-piyasasi-nedir",
    enSlug: "what-is-a-carbon-market",
    url: "https://akillisurdurulebilirlik.com/yazilar/karbon-piyasasi-nedir",
    internal: true,
  },
  {
    iso: "2026-08-25",
    dateTr: "25 Ağustos 2026",
    dateEn: "25 August 2026",
    titleTr: "COP31: İki ülke, iki rol, tek masa",
    titleEn: "COP31: Two Countries, Two Roles, One Table",
    summaryTr:
      "Antalya modeli, COP başkanlığının ev sahipliği ve arabuluculuk rollerini ilk kez bu kadar açık biçimde iki ülkeye ayırıyor. Türkiye siyasal sahneyi, Avustralya müzakere masasını yönetecek; asıl sınav, bu iki elin tek bir anlaşmayı düşürmeden tamamlayıp tamamlayamayacağı.",
    summaryEn:
      "The Antalya model separates the COP presidency's two core functions more explicitly than ever: Türkiye runs the political stage, while Australia runs the negotiating table. The real test is whether those two hands can conclude one global agreement without letting it fall between them.",
    slug: "cop31-two-countries-two-roles-one-table",
    enSlug: "cop31-two-countries-two-roles-one-table",
  },
  {
    iso: "2026-08-19",
    dateTr: "19 Ağustos 2026",
    dateEn: "19 August 2026",
    titleTr: "Mavi karbon nedir?",
    titleEn: "What Is Blue Carbon?",
    summaryTr:
      "Deniz çayırları ve kıyı ekosistemleri yıllar boyunca karbon biriktirir; bozulduklarında tortudaki organik karbon yeniden parçalanır ve atmosfere döner. Mavi karbon, COP31'in sessiz ama kritik başlığı.",
    summaryEn:
      "Seagrass meadows and coastal ecosystems store carbon for decades; once disturbed, sediment carbon breaks down and returns to the atmosphere. Blue carbon is COP31's quiet but critical file.",
    slug: "mavi-karbon-nedir",
    enSlug: "what-is-blue-carbon",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/mavi-karbon-nedir-85726",
  },
  {

    iso: "2026-08-12",
    dateTr: "12 Ağustos 2026",
    dateEn: "12 August 2026",
    titleTr: "COP31'e üç ay kala tablo nasıl?",
    titleEn: "Three Months to COP31: Where Things Actually Stand",
    summaryTr:
      "9–20 Kasım'da Antalya'da yapılacak zirvenin çerçevesi belirginleşti: Türkiye ev sahibi ve Başkan, Avustralya Müzakereler Başkanı. Bonn'da Küresel Uyum Hedefi göstergelerinde uzlaşma sağlanamadı; dosyalar Antalya'ya kaldı.",
    summaryEn:
      "The shape of the 9–20 November summit in Antalya is now largely clear: Türkiye holds the Presidency as host, Australia presides over the negotiations. Bonn failed to converge on the Global Goal on Adaptation, so those files travel to Antalya unresolved.",
    slug: "cop31e-uc-ay-kala",
    enSlug: "three-months-to-cop31-where-things-actually-stand",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/cop31e-uc-ay-kala-tablo-nasil-85191",
  },
  {
    iso: "2026-08-07",
    dateTr: "7 Ağustos 2026",
    dateEn: "7 August 2026",
    titleTr: "Yeşil aklama artık ahlâk değil, ispat meselesi!",
    titleEn: "Greenwashing Is No Longer an Ethics Question — It Is a Burden of Proof",
    summaryTr:
      "1 Ağustos'tan itibaren \"doğa dostu\", \"karbon nötr\" veya \"sürdürülebilir\" gibi ifadeler kanıt gerektiriyor. COP31'in arka planında da bu dönüşüm var: taahhütlerin güvenilirliği, iddianın kanıtıyla ölçülecek.",
    summaryEn:
      "As of 1 August, claims such as \"nature-friendly\", \"carbon neutral\" or \"sustainable\" require evidence in Türkiye. That shift sits underneath COP31: the credibility of a pledge is now measured by the evidence behind the claim.",
    slug: "yesil-aklama-ispat-meselesi",
    enSlug: "greenwashing-is-a-burden-of-proof",
    url: "https://akillisurdurulebilirlik.com/yazilar/yesil-aklama-artik-ahlak-degil-ispat-meselesi",
    internal: true,
  },
  {
    iso: "2026-07-08",
    dateTr: "8 Temmuz 2026",
    dateEn: "8 July 2026",
    titleTr: "Yeşil Finans Stratejisi ne diyor?",
    titleEn: "What Does the Green Finance Strategy Actually Say?",
    summaryTr:
      "Yayımlanan strateji ve eylem belgesinin, İklim Kanunu ile omuz omuza COP31 açısından kritik önemi var. Ev sahibinin elindeki en güçlü diplomatik enstrüman bu; \"model ülke olmak\" iddiası ancak bu somutlukla taşınabilir.",
    summaryEn:
      "Read alongside the Climate Law, Türkiye's green finance strategy is the host country's strongest diplomatic instrument at COP31. The claim to become a \"model country\" only holds if it is carried by that level of concreteness.",
    slug: "yesil-finans-stratejisi",
    enSlug: "what-does-the-green-finance-strategy-say",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/yesil-finans-stratejisi-ne-diyor-82719",
  },
  {
    iso: "2026-07-01",
    dateTr: "1 Temmuz 2026",
    dateEn: "1 July 2026",
    titleTr: "Su gidince",
    titleEn: "When the Water Is Gone",
    summaryTr:
      "Ankara'ya 45 dakika mesafedeki küçük gölün yerinde çatlamış bir çukur, kurumuş bir yatak vardı. Su gitmişti; onunla beraber, herkes.",
    summaryEn:
      "A childhood memory of a small lake 45 minutes from Ankara. Years later the road is the same, but the lake is gone: a cracked hollow, a dry bed. Water left, and with it, everyone.",
    slug: "su-gidince",
    enSlug: "when-the-water-is-gone",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/su-gidince-82218",
  },
  {
    iso: "2026-05-06",
    dateTr: "6 Mayıs 2026",
    dateEn: "6 May 2026",
    titleTr: "Yıkıcı bilanço: 30'a 1!",
    titleEn: "A Ruinous Balance Sheet: 30 to 1",
    summaryTr:
      "Doğayı korumaya ayrılan her 1 dolara karşılık tahrip eden faaliyetlere yaklaşık 30 dolar akıyor: 7,3 trilyon dolarlık yıkıcı finansmana karşı 220 milyar dolarlık koruma yatırımı (UNEP, State of Finance for Nature 2026).",
    summaryEn:
      "For every dollar spent protecting nature, roughly thirty dollars flow to activities that destroy it: USD 7.3 trillion in destructive finance against USD 220 billion in protective investment (UNEP, State of Finance for Nature 2026).",
    slug: "yikici-bilanco-30a-1",
    enSlug: "a-ruinous-balance-sheet-30-to-1",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/yikici-bilanco-30a-1-78675",
  },
  {
    iso: "2026-04-29",
    dateTr: "29 Nisan 2026",
    dateEn: "29 April 2026",
    titleTr: "Coğrafya kader değil, stratejidir",
    titleEn: "Geography Is Not Destiny, It Is Strategy",
    summaryTr:
      "Türkiye, iklim krizinin üç cephesinin kesiştiği bir coğrafyada: Akdeniz iklim kırılganlığı, enerji güvenliği ve finansman. COP31, bu kırılganlığı tutarlı bir bölgesel çerçeveye dönüştürme fırsatı.",
    summaryEn:
      "Türkiye sits where three fronts of the climate crisis intersect: Mediterranean vulnerability, energy security and finance. COP31 is an opening to turn that vulnerability into a coherent regional framework.",
    slug: "cografya-kader-degil-strateji",
    enSlug: "geography-is-not-destiny-it-is-strategy",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/cografya-kader-degil-stratejidir-78122",
  },
  {
    iso: "2026-03-25",
    dateTr: "25 Mart 2026",
    dateEn: "25 March 2026",
    titleTr: "Kırılgan yaşam üçgeni: Su, enerji, tarım",
    titleEn: "The Fragile Triangle: Water, Energy, Agriculture",
    summaryTr:
      "Ulusal Su Planı (2026–2035) yayımlandı: verimlilik, tasarruf, dijitalleşme, koordinasyon. Konu sadece su değil; aynı zamanda enerji, gıda ve ekosistem meselesi.",
    summaryEn:
      "Türkiye's National Water Plan (2026–2035) is out: efficiency, savings, digitalisation, coordination. The issue is not only water; it is energy, food and ecosystems at once.",
    slug: "kirilgan-yasam-ucgeni",
    enSlug: "the-fragile-triangle-water-energy-agriculture",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/kirilgan-yasam-ucgeni-su-enerji-tarim-75429",
  },
];

export const cop31NotesSorted = [...cop31Notes].sort(
  (a, b) => new Date(b.iso).getTime() - new Date(a.iso).getTime(),
);

export function getCop31Note(slug: string): Cop31Note | undefined {
  return cop31Notes.find((n) => n.slug === slug || n.enSlug === slug);
}

export function cop31NoteSlug(slug: string, lang: "tr" | "en"): string {
  const note = getCop31Note(slug);
  return (lang === "en" ? note?.enSlug : note?.slug) ?? slug;
}

/** Permalink for a note in the given locale. */
export function cop31NotePath(slug: string, lang: "tr" | "en"): string {
  const localized = cop31NoteSlug(slug, lang);
  return lang === "tr" ? `/cop31/notlar/${localized}` : `/cop31-guide/notes/${localized}`;
}

/**
 * Machine-interface view of the analysis layer (MCP tools, LLM crawlers).
 * Derived from the same array the guide pages and the RSS feeds read, so a note
 * added once is indexed everywhere by the same trigger.
 */
export interface Cop31AnalysisNote {
  slug: string;
  date: string;
  url?: string;
  title_tr: string;
  title_en: string;
  summary_tr: string;
  summary_en: string;
  locale?: "both" | "en";
  internal?: boolean;
}

export const COP31_ANALYSIS: Cop31AnalysisNote[] = cop31NotesSorted.map((n) => ({
  slug: n.slug,
  date: n.iso,
  url: n.url,
  title_tr: n.titleTr,
  title_en: n.titleEn,
  summary_tr: n.summaryTr,
  summary_en: n.summaryEn,
  locale: n.locale,
  internal: n.internal,
}));
